import { create } from 'zustand';
import type { Route, TrackerUser, MQTTConfig, RouteStatus, AppSettings } from '../types';

interface AppState {
  routes: Route[];
  activeRouteId: string | null;
  users: TrackerUser[];
  mqttConfigs: MQTTConfig[];
  activePanel: 'settings' | 'map' | 'users' | null;
  isAddingPoint: boolean;
  pointType: 'start' | 'checkpoint' | 'goal';
  settings: AppSettings;
  activeRoutes: Map<string, Set<string>>; // Changed to Map<userId, Set<routeId>>
  setRoutes: (routes: Route[]) => void;
  addRoute: (route: Route) => void;
  updateRoute: (route: Route) => void;
  deleteRoute: (id: string) => void;
  setActiveRoute: (id: string | null) => void;
  setUsers: (users: TrackerUser[]) => void;
  setActivePanel: (panel: 'settings' | 'map' | 'users' | null) => void;
  setIsAddingPoint: (isAdding: boolean) => void;
  setPointType: (type: 'start' | 'checkpoint' | 'goal') => void;
  updateUserPosition: (userId: string, position: [number, number], speed: number, course: number) => void;
  updateUserRouteStatus: (userId: string, routeStatus: RouteStatus | null) => void;
  updateSettings: (settings: Partial<AppSettings>) => void;
  updateUserRouteCompletion: (userId: string, routeId: string, completed: boolean) => void;
  addUsedResetPoint: (userId: string, routeId: string, resetPointId: string) => void;
  toggleRouteActive: (userId: string, routeId: string) => void;
  setAllRoutesActive: (userId: string, active: boolean) => void;
  getActiveRoutesForUser: (userId: string) => Set<string>;
}

const STORAGE_KEYS = {
  SETTINGS: 'marathon-tracker-settings',
  ACTIVE_ROUTES: 'marathon-tracker-active-routes'
};

const DEFAULT_SETTINGS: AppSettings = {
  routeTolerance: 10,
  checkpointTolerance: 3
};

const loadSettings = (): AppSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error loading settings:', error);
    return DEFAULT_SETTINGS;
  }
};

const loadActiveRoutes = (): Map<string, Set<string>> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.ACTIVE_ROUTES);
    if (saved) {
      const parsed = JSON.parse(saved);
      const map = new Map();
      Object.entries(parsed).forEach(([userId, routes]) => {
        map.set(userId, new Set(routes as string[]));
      });
      return map;
    }
    return new Map();
  } catch (error) {
    console.error('Error loading active routes:', error);
    return new Map();
  }
};

const saveActiveRoutes = (activeRoutes: Map<string, Set<string>>) => {
  try {
    const data: { [key: string]: string[] } = {};
    activeRoutes.forEach((routes, userId) => {
      data[userId] = Array.from(routes);
    });
    localStorage.setItem(STORAGE_KEYS.ACTIVE_ROUTES, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving active routes:', error);
  }
};

export const useStore = create<AppState>()((set, get) => ({
  routes: [],
  activeRouteId: null,
  users: [],
  mqttConfigs: [],
  activePanel: 'settings',
  isAddingPoint: false,
  pointType: 'start',
  settings: loadSettings(),
  activeRoutes: loadActiveRoutes(),

  setRoutes: (routes) => {
    const state = get();
    const activeRoutes = new Map(state.activeRoutes);
    let updated = false;

    state.users.forEach(user => {
      if (!activeRoutes.has(user.id)) {
        activeRoutes.set(user.id, new Set());
      }
      const userRoutes = activeRoutes.get(user.id)!;
      routes.forEach(route => {
        if (!userRoutes.has(route.id)) {
          userRoutes.add(route.id);
          updated = true;
        }
      });
    });

    if (updated) {
      saveActiveRoutes(activeRoutes);
    }
    set({ routes, activeRoutes });
  },

  addRoute: (route) =>
    set((state) => ({ routes: [...state.routes, route] })),

  updateRoute: (route) =>
    set((state) => ({
      routes: state.routes.map((r) => (r.id === route.id ? route : r)),
    })),

  deleteRoute: (id) =>
    set((state) => {
      const activeRoutes = new Map(state.activeRoutes);
      activeRoutes.forEach((routes) => routes.delete(id));
      saveActiveRoutes(activeRoutes);
      return {
        routes: state.routes.filter((r) => r.id !== id),
        activeRouteId: state.activeRouteId === id ? null : state.activeRouteId,
        isAddingPoint: state.activeRouteId === id ? false : state.isAddingPoint,
        activeRoutes
      };
    }),

  setActiveRoute: (id) =>
    set({ activeRouteId: id }),

  setUsers: (users) => {
    set((state) => {
      const activeRoutes = new Map(state.activeRoutes);
      users.forEach(user => {
        if (!activeRoutes.has(user.id)) {
          activeRoutes.set(user.id, new Set(state.routes.map(r => r.id)));
        }
      });
      saveActiveRoutes(activeRoutes);
      return { users, activeRoutes };
    });
  },

  setActivePanel: (panel) =>
    set({ activePanel: panel }),

  setIsAddingPoint: (isAdding) =>
    set({ isAddingPoint: isAdding }),

  setPointType: (type) =>
    set({ pointType: type }),

  updateUserPosition: (userId, position, speed, course) =>
    set((state) => ({
      users: state.users.map((user) =>
        user.id === userId
          ? {
              ...user,
              currentPosition: position,
              currentSpeed: speed,
              currentCourse: course
            }
          : user
      ),
    })),

  updateUserRouteStatus: (userId, routeStatus) =>
    set((state) => {
      const user = state.users.find(u => u.id === userId);
      if (!user) return state;

      const userActiveRoutes = state.activeRoutes.get(userId) || new Set();

      if (routeStatus?.routeId && !userActiveRoutes.has(routeStatus.routeId)) {
        return state;
      }

      if (routeStatus?.onRoute && routeStatus.distanceToGoal === 0) {
        const currentRoute = state.routes.find(r => r.name === routeStatus.name);
        if (currentRoute) {
          const completedRoutes = [...(user.completedRoutes || []), currentRoute.id];
          
          userActiveRoutes.delete(currentRoute.id);
          const newActiveRoutes = new Map(state.activeRoutes);
          newActiveRoutes.set(userId, userActiveRoutes);
          saveActiveRoutes(newActiveRoutes);

          const nextRoute = state.routes
            .filter(r => !completedRoutes.includes(r.id) && userActiveRoutes.has(r.id))
            .sort((a, b) => {
              const aNum = parseInt(a.name.replace(/[^0-9]/g, '')) || 0;
              const bNum = parseInt(b.name.replace(/[^0-9]/g, '')) || 0;
              return aNum - bNum;
            })[0];

          return {
            users: state.users.map((u) =>
              u.id === userId
                ? {
                    ...u,
                    completedRoutes,
                    lastRouteStatus: nextRoute ? {
                      ...routeStatus,
                      name: nextRoute.name,
                      color: nextRoute.color,
                      completed: false,
                      routeId: nextRoute.id
                    } : routeStatus,
                    currentRoute: nextRoute?.name
                  }
                : u
            ),
            activeRoutes: newActiveRoutes
          };
        }
      }

      return {
        users: state.users.map((u) =>
          u.id === userId
            ? {
                ...u,
                lastRouteStatus: routeStatus,
                currentRoute: routeStatus?.onRoute ? routeStatus.name : undefined
              }
            : u
        ),
      };
    }),

  updateSettings: (newSettings) =>
    set((state) => {
      const settings = { ...state.settings, ...newSettings };
      try {
        localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
      } catch (error) {
        console.error('Error saving settings:', error);
      }
      return { settings };
    }),

  updateUserRouteCompletion: (userId, routeId, completed) =>
    set((state) => {
      const userActiveRoutes = new Set(state.activeRoutes.get(userId) || new Set());
      if (completed) {
        userActiveRoutes.delete(routeId);
        const newActiveRoutes = new Map(state.activeRoutes);
        newActiveRoutes.set(userId, userActiveRoutes);
        saveActiveRoutes(newActiveRoutes);
      }

      return {
        users: state.users.map((user) =>
          user.id === userId
            ? {
                ...user,
                completedRoutes: completed
                  ? [...(user.completedRoutes || []), routeId]
                  : (user.completedRoutes || []).filter(id => id !== routeId)
              }
            : user
        ),
        activeRoutes: state.activeRoutes
      };
    }),

  addUsedResetPoint: (userId, routeId, resetPointId) =>
    set((state) => ({
      users: state.users.map((user) =>
        user.id === userId
          ? {
              ...user,
              usedResetPoints: {
                ...(user.usedResetPoints || {}),
                [routeId]: new Set([
                  ...(user.usedResetPoints?.[routeId] || []),
                  resetPointId
                ])
              }
            }
          : user
      ),
    })),

  toggleRouteActive: (userId, routeId) =>
    set((state) => {
      const userActiveRoutes = new Set(state.activeRoutes.get(userId) || new Set());
      if (userActiveRoutes.has(routeId)) {
        userActiveRoutes.delete(routeId);
      } else {
        userActiveRoutes.add(routeId);
      }
      const newActiveRoutes = new Map(state.activeRoutes);
      newActiveRoutes.set(userId, userActiveRoutes);
      saveActiveRoutes(newActiveRoutes);
      return { activeRoutes: newActiveRoutes };
    }),

  setAllRoutesActive: (userId, active) =>
    set((state) => {
      const userActiveRoutes = new Set(active ? state.routes.map(r => r.id) : []);
      const newActiveRoutes = new Map(state.activeRoutes);
      newActiveRoutes.set(userId, userActiveRoutes);
      saveActiveRoutes(newActiveRoutes);
      return { activeRoutes: newActiveRoutes };
    }),

  getActiveRoutesForUser: (userId) => {
    const state = get();
    return state.activeRoutes.get(userId) || new Set();
  },
}));